import { type FC, type ReactNode } from 'react'
import { useFlags } from '@egw/feature-flags'
import { type FeatureFlags } from '@egw/constants'
import { NotFound404 } from '@egw/features-system'

interface FeatureFlagGuardProps {
  /** The feature flag key to check */
  feature: keyof FeatureFlags
  /** Component to render when feature flag is enabled */
  children: ReactNode
  /** Component to render when feature flag is disabled (defaults to NotFound404) */
  fallback?: ReactNode
}

/**
 * Generic feature flag guard component that conditionally renders content
 * based on feature flag status. Shows fallback (default: 404) when flag is off.
 * 
 * @example
 * <FeatureFlagGuard feature="rolloutReentryV2">
 *   <MyComponent />
 * </FeatureFlagGuard>
 */
export const FeatureFlagGuard: FC<FeatureFlagGuardProps> = ({ 
  feature, 
  children, 
  fallback = <NotFound404 /> 
}) => {
  const { flags } = useFlags<FeatureFlags>()

  return flags[feature] ? <>{children}</> : <>{fallback}</>
}
